/**
 * Incremental parser for the server-sent events wire format
 * (https://html.spec.whatwg.org/multipage/server-sent-events.html#event-stream-interpretation).
 *
 * `EventSource` can't send an Authorization header, so authenticated streams are read with `fetch` and fed through
 * this parser chunk by chunk. Chunks may split lines and events anywhere.
 */

export type SseEvent = {
  /** The `event:` field, or "message" when the server sent none. */
  event: string;
  data: string;
  id?: string;
  /** The server's requested reconnection delay, in milliseconds. */
  retry?: number;
};

export type SseParser = {
  /** Feeds decoded text. Calls `onEvent` for every event the text completes. */
  push: (chunk: string) => void;
  /** Drops any partial event, e.g. after the connection drops. */
  reset: () => void;
};

export const createSseParser = (onEvent: (event: SseEvent) => void): SseParser => {
  let buffer = '';
  let eventType = '';
  let data: string[] = [];
  let id: string | undefined;
  let retry: number | undefined;
  let firstChunk = true;

  const dispatch = () => {
    // a blank line with no data lines is a keepalive or comment-only block
    if (data.length > 0) {
      onEvent({event: eventType || 'message', data: data.join('\n'), id, retry});
    }
    eventType = '';
    data = [];
    retry = undefined;
  };

  const processLine = (line: string) => {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) {
      return;
    }

    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }

    switch (field) {
      case 'event':
        eventType = value;
        break;
      case 'data':
        data.push(value);
        break;
      case 'id':
        // per spec, an id containing NUL is ignored
        if (!value.includes('\0')) id = value;
        break;
      case 'retry':
        if (/^\d+$/.test(value)) retry = Number(value);
        break;
      default:
        break;
    }
  };

  return {
    push: (chunk: string) => {
      if (firstChunk) {
        firstChunk = false;
        if (chunk.charCodeAt(0) === 0xfeff) chunk = chunk.slice(1);
      }

      buffer += chunk;

      let start = 0;
      for (let i = 0; i < buffer.length; i++) {
        const ch = buffer[i];
        if (ch !== '\n' && ch !== '\r') continue;

        // a lone CR at the very end may be the first half of CRLF; wait for the next chunk
        if (ch === '\r' && i === buffer.length - 1) break;

        processLine(buffer.slice(start, i));
        if (ch === '\r' && buffer[i + 1] === '\n') i++;
        start = i + 1;
      }

      buffer = buffer.slice(start);
    },
    reset: () => {
      buffer = '';
      eventType = '';
      data = [];
      retry = undefined;
    },
  };
};
